import { Badge, Box, Button, Divider, IconButton, MenuItem, Popover, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import type { NotificationRecord } from "@xop/dashboard-shared";
import { useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api.js";
import { relativeTime } from "../lib/relativeTime.js";
import {
  focusDesktopWindow,
  testDesktopNotification,
} from "../lib/notifications/desktopBridge.js";
import {
  useClearNotifications,
  useDismissNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsFeed,
} from "../lib/notifications/useNotifications.js";
import { ConfirmDialog } from "./ui/confirm-dialog.js";

/** Header bell: live unread badge + a popover of the most recent
 * notifications. Clicking an item marks it read and deep-links to it
 * (focusing the desktop window first when running in the desktop app). */
export function NotificationBell(): JSX.Element {
  const { data } = useNotificationsFeed(1);
  const markAll = useMarkAllNotificationsRead();
  const markRead = useMarkNotificationRead();
  const dismiss = useDismissNotification();
  const clearAll = useClearNotifications();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const open = Boolean(anchorEl);

  const unread = data?.unread ?? 0;
  const total = data?.total ?? 0;
  const items = (data?.items ?? []).slice(0, 8);

  const openItem = (n: NotificationRecord): void => {
    if (!n.read_at) markRead.mutate(n.id);
    void focusDesktopWindow();
    setAnchorEl(null);
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <IconButton
        type="button"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        sx={{
          height: 40,
          width: 40,
          borderRadius: 1.5,
          color: "text.secondary",
          "&:hover": { bgcolor: "action.hover", color: "text.primary" },
        }}
      >
        <Badge
          badgeContent={unread > 99 ? "99+" : unread}
          color="error"
          overlap="circular"
          sx={{ "& .MuiBadge-badge": { fontSize: 11, fontWeight: 700, minWidth: 18, height: 18 } }}
        >
          <NotificationsNoneIcon sx={{ fontSize: 24 }} aria-hidden />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 320, mt: 0.5 } } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
            Notifications
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              type="button"
              size="small"
              variant="text"
              color="inherit"
              onClick={() => {
                // Two-pronged test: (1) server → SSE → bell + on-screen popup
                // (the full live pipeline); (2) a direct local bottom-right
                // popup (isolates the desktop-popup render from the SSE path).
                void api("/api/notifications/test", { method: "POST" });
                void testDesktopNotification();
              }}
              title="Send yourself a test notification (verifies desktop popups)"
              sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: 12, color: "text.disabled" }}
            >
              Test
            </Button>
            {unread > 0 && (
              <Button
                type="button"
                size="small"
                variant="text"
                color="inherit"
                startIcon={<DoneAllIcon sx={{ fontSize: 14 }} />}
                onClick={() => markAll.mutate()}
                sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: 12, color: "text.disabled" }}
              >
                Mark all read
              </Button>
            )}
            {total > 0 && (
              <Tooltip title="Clear all notifications">
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Clear all notifications"
                  onClick={() => setConfirmClearOpen(true)}
                  sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
                >
                  <DeleteSweepIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Divider />

        {items.length === 0 ? (
          <Typography sx={{ px: 1.5, py: 3, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
            You&apos;re all caught up.
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, maxHeight: 320, overflowY: "auto", py: 0.5 }}>
            {items.map((n) => (
              <Box
                component="li"
                key={n.id}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  "&:hover": { bgcolor: "action.hover" },
                  "&:hover .dismiss-btn": { opacity: 1 },
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => openItem(n)}
                  sx={{
                    display: "flex",
                    flex: 1,
                    minWidth: 0,
                    alignItems: "flex-start",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    border: 0,
                    bgcolor: "transparent",
                    textAlign: "left",
                    cursor: "pointer",
                    font: "inherit",
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      mt: "6px",
                      height: 6,
                      width: 6,
                      flexShrink: 0,
                      borderRadius: "50%",
                      bgcolor: n.read_at ? "transparent" : "primary.main",
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                      {n.title}
                    </Box>
                    <Box sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "text.disabled" }}>
                      {n.body}
                    </Box>
                    <Box sx={{ mt: 0.25, display: "block", fontSize: 11, color: "text.disabled" }}>
                      {relativeTime(n.created_at)}
                    </Box>
                  </Box>
                </Box>
                <IconButton
                  type="button"
                  size="small"
                  className="dismiss-btn"
                  aria-label={`Dismiss: ${n.title}`}
                  onClick={() => dismiss.mutate(n.id)}
                  sx={{
                    alignSelf: "center",
                    mr: 0.5,
                    color: "text.disabled",
                    // Quiet by default on pointer devices, revealed on row
                    // hover; always visible on touch (no hover).
                    opacity: { xs: 1, md: 0 },
                    transition: "opacity 120ms",
                    "&:hover": { color: "error.main", bgcolor: "transparent" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate("/profile/notifications");
          }}
          sx={{ justifyContent: "center", fontSize: 14 }}
        >
          Notification settings
        </MenuItem>
      </Popover>

      <ConfirmDialog
        isOpen={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        title="Clear all notifications?"
        description={
          total > 0
            ? `This permanently removes all ${total} notification${total === 1 ? "" : "s"} from your bell. This can't be undone.`
            : "This permanently removes every notification from your bell. This can't be undone."
        }
        variant="destructive"
        confirmLabel="Clear all"
        onConfirm={async () => {
          await clearAll.mutateAsync();
          setAnchorEl(null);
        }}
      />
    </>
  );
}
